<template>
  <TransitionGroup
    tag="div"
    :class="$style.stack"
    :enter-active-class="$style.enterActive"
    :enter-from-class="$style.enterFrom"
    :leave-active-class="$style.leaveActive"
    :leave-to-class="$style.leaveTo"
    :move-class="$style.move"
    appear
  >
    <ProgressToastCard
      v-if="showUpload"
      key="upload"
      test-id="file-upload-progress-toast"
      :title="$t('fileExplorer.upload.progressTitle', { done: queue.uploadedCount, total: queue.uploadTotal })"
      :subtitle="formatBytes(queue.bytesTotal)"
      :percentage="queue.uploadPercentage"
      @close="dismissedUpload = true"
    >
      <template #icon>
        <SCASvgIcon :class="$style.icon" :icon="uploadIcon" size="40px" />
      </template>
    </ProgressToastCard>

    <ProgressToastCard
      v-if="showAi"
      key="ai"
      test-id="file-ai-progress-toast"
      :title="$t('fileExplorer.upload.aiProgressTitle', { done: queue.aiProcessedCount, total: queue.aiTotal })"
      :subtitle="formatBytes(aiBytesTotal)"
      :percentage="queue.aiPercentage"
      @close="dismissedAi = true"
    >
      <template #icon>
        <div :class="$style.sparkleIcon">
          <SCASvgIcon :class="$style.icon" icon="phosphor/sparkle" size="40px" />
        </div>
      </template>
    </ProgressToastCard>

    <div v-if="showSuccess" key="success" :class="[$style.toast, $style.success]" test-id="file-upload-success-toast">
      <div :class="$style.row">
        <SCASvgIcon icon="phosphor/check-circle" size="40px" />
        <div :class="$style.content">
          <div :class="$style.titleRow">
            <div :class="$style.title">{{ $t("fileExplorer.upload.completedTitle") }}</div>
            <button
              type="button"
              :class="$style.closeButton"
              :title="$t('common.action.close')"
              :aria-label="$t('common.action.close')"
              test-id="file-upload-success-toast-close"
              @click="showSuccess = false"
            >
              <SCASvgIcon icon="phosphor/x" size="16px" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="failure" key="failure" :class="[$style.toast, $style.failure]" test-id="file-upload-failure-toast">
      <div :class="$style.row">
        <SCASvgIcon icon="phosphor/warning-circle" size="40px" />
        <div :class="$style.content">
          <div :class="$style.titleRow">
            <div :class="$style.title">
              {{ $t("fileExplorer.upload.failedTitle", { failed: failure.items.length, total: failure.total }) }}
            </div>
            <button
              type="button"
              :class="$style.closeButton"
              :title="$t('common.action.close')"
              :aria-label="$t('common.action.close')"
              test-id="file-upload-failure-toast-close"
              @click="failure = null"
            >
              <SCASvgIcon icon="phosphor/x" size="16px" />
            </button>
          </div>
        </div>
      </div>
      <ul :class="$style.failureList">
        <!-- Index as key: two files of one batch can share a name. -->
        <li
          v-for="(item, index) in failure.items"
          :key="index"
          :class="$style.failureItem"
          :title="`${item.reason} — ${item.name}`"
        >
          {{ item.reason }} — {{ item.name }}
        </li>
      </ul>
    </div>

    <!-- Unrelated long-running operations (tenant copy, etc.) share this same stack rather than a
         second fixed-position toast, which would otherwise overlap this one in the same corner. -->
    <ProgressToastCard
      v-for="operation in asyncProgress.operations"
      :key="operation.key"
      test-id="async-progress-toast"
      :title="operation.title"
      :subtitle="operation.subtitle"
      :percentage="operation.percentage"
      @close="asyncProgress.remove(operation.id)"
    >
      <template #icon>
        <SCASvgIcon :class="$style.icon" :icon="operation.icon" size="40px" />
      </template>
    </ProgressToastCard>
  </TransitionGroup>
</template>

<script setup lang="ts">
import { SCASvgIcon, TOAST_DURATION_MS } from "@scayla/components";
import { formatBytes } from "@scayla/core";
import { computed, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import useClassificationProgressEvents from "@/components/progress/composables/useClassificationProgressEvents";
import useTenantCopyProgressEvents from "@/components/progress/composables/useTenantCopyProgressEvents";
import useTenantDeleteProgressEvents, {
  TENANT_DELETE_OPERATION_KIND,
} from "@/components/progress/composables/useTenantDeleteProgressEvents";
import ProgressToastCard from "@/components/progress/ProgressToastCard.vue";
import useRecordBulkUpdateEvents, { RECORD_BULK_UPDATE_OPERATION } from "@/composables/useRecordBulkUpdateEvents";
import { useAsyncOperationProgress } from "@/store/asyncOperationProgress";
import { useFileUploadQueue } from "@/store/fileUploadQueue";
import { getIconByMimeType } from "@/utils/fileAttachments";

import { useLazyEvents } from "./composables/useLazyEvents";

/**
 * How long a finished progress popup stays once whatever succeeds it is up — the completion toast,
 * or the AI popup of the same run. Short overlap on purpose: the final count stays readable beside
 * the confirmation, then the stack collapses to the confirmation alone.
 */
const PROGRESS_LINGER_MS = 2000;

const { t: $t } = useI18n();
const queue = useFileUploadQueue();
const asyncProgress = useAsyncOperationProgress();

/**
 * What went wrong in the run that just settled, snapshotted before the queue is reset — the store
 * drops its items a few seconds later, and the popup has to outlive them.
 */
interface FailureSummary {
  total: number;
  items: { name: string; reason: string }[];
}

const failure = ref<FailureSummary | null>(null);
const showSuccess = ref(false);
const lingerUpload = ref(false);
const lingerAi = ref(false);
/** Set when the user closes a popup by hand — the upload/AI processing keeps running behind it. */
const dismissedUpload = ref(false);
const dismissedAi = ref(false);
let successTimer: ReturnType<typeof setTimeout> | undefined;
let failureTimer: ReturnType<typeof setTimeout> | undefined;
let uploadLingerTimer: ReturnType<typeof setTimeout> | undefined;
let aiLingerTimer: ReturnType<typeof setTimeout> | undefined;
function hasRunningOperation(kind: string) {
  return asyncProgress.operations.some((o) => o.kind === kind && o.status === "running");
}

const showUpload = computed(() => (queue.hasActiveUpload || lingerUpload.value) && !dismissedUpload.value);
/**
 * Suppressed by an upload that is actually running, not by the finished upload's linger: gated on
 * `showUpload` the two popups shared one flag, so a run whose classification settles immediately
 * (rejected by the provider, duplicate, low confidence) had its AI popup swallowed — both linger
 * timers expired in the same tick and it never rendered at all.
 */
const showAi = computed(
  () => queue.aiTotal > 0 && !queue.hasActiveUpload && (queue.hasActiveAi || lingerAi.value) && !dismissedAi.value,
);

/** Combined size of the files actually going through AI processing, not the whole upload batch. */
const aiBytesTotal = computed(() =>
  queue.items.filter((item) => item.aiRequested).reduce((sum, item) => sum + item.size, 0),
);

/**
 * Icon of the file still in flight, mirroring the design's per-file thumbnail. Once every file has
 * settled (the linger period showing the finished count), there is no "in flight" file left, so this
 * falls back to the last queued file instead of a blank generic icon.
 */
const uploadIcon = computed(() => {
  const pending = queue.items.find((item) => item.status !== "uploaded" && item.status !== "failed");
  const item = pending ?? queue.items.at(-1);
  return getIconByMimeType(item?.file.type ?? "");
});

watch(
  () => queue.hasActiveUpload,
  (active) => {
    clearTimeout(uploadLingerTimer);
    if (active) {
      lingerUpload.value = true;
      return;
    }
    uploadLingerTimer = setTimeout(() => (lingerUpload.value = false), PROGRESS_LINGER_MS);
  },
);

watch(
  () => queue.hasActiveAi,
  (active) => {
    clearTimeout(aiLingerTimer);
    if (active) {
      lingerAi.value = true;
      return;
    }
    aiLingerTimer = setTimeout(() => (lingerAi.value = false), PROGRESS_LINGER_MS);
  },
);

/**
 * A dismissal covers the run it was aimed at; submitting more files re-arms the popup. Watched on
 * the counts rather than on `hasActiveUpload`/`hasActiveAi`: enqueue appends to a run already in
 * flight, so those flags never dip between batches and every batch submitted before the run settles
 * would stay suppressed.
 */
watch(
  () => queue.uploadTotal,
  (total, previous) => {
    if (total > previous) dismissedUpload.value = false;
  },
);

watch(
  () => queue.aiTotal,
  (total, previous) => {
    if (total > previous) dismissedAi.value = false;
  },
);

/**
 * The stream is only open while a batch needs it: the queue is the only consumer, so an idle tab
 * keeps no extra EventSource around. The same goes for tenant copies and deletes and bulk record updates.
 */
useLazyEvents(() => queue.aiTotal > 0 && !queue.isAiFinished, useClassificationProgressEvents);
useLazyEvents(() => hasRunningOperation("tenant-copy"), useTenantCopyProgressEvents);
useLazyEvents(() => hasRunningOperation(RECORD_BULK_UPDATE_OPERATION), useRecordBulkUpdateEvents);
useLazyEvents(() => hasRunningOperation(TENANT_DELETE_OPERATION_KIND), useTenantDeleteProgressEvents);

/** Success confirmation appears once the whole batch is done, AI included when it was requested. */
watch(
  () => queue.uploadTotal > 0 && queue.isUploadFinished && (queue.aiTotal === 0 || queue.isAiFinished),
  (finished) => {
    if (!finished) {
      // A new batch was enqueued while the previous one was still winding down. Its scheduled reset
      // would clear the queue mid-run — taking the new batch's files with it, so its classification
      // never even starts — so the moment the queue is busy again that reset is called off. The
      // confirmation goes with it: it belongs to the batch that finished, not to the one now running.
      clearTimeout(successTimer);
      showSuccess.value = false;
      return;
    }

    const hadFailures = captureFailures();

    // Resetting has to wait out the linger above: it clears the items the popups read their final
    // numbers from.
    clearTimeout(successTimer);

    // Not "fewer failures than files": a batch where some files failed did not complete, and
    // pairing a green confirmation with the failure popup below tells the user two opposite things.
    if (!hadFailures) {
      showSuccess.value = true;
      successTimer = setTimeout(() => {
        showSuccess.value = false;
        queue.reset();
      }, TOAST_DURATION_MS);
      return;
    }

    successTimer = setTimeout(() => queue.reset(), TOAST_DURATION_MS);
  },
);

/**
 * Snapshots what failed so the failure popup can outlive queue.reset(). A clean run leaves an
 * earlier run's popup standing rather than clearing it, on its own timer.
 *
 * @returns whether this run had any failure, which is what decides the green confirmation.
 */
function captureFailures(): boolean {
  const failed = queue.items
    .filter((item) => item.status === "failed" || item.aiStatus === "failed")
    .map((item) => ({
      name: item.name,
      reason: item.errorMessage ?? $t("fileExplorer.upload.failedReasonUnknown"),
    }));

  if (failed.length === 0) return false;

  failure.value = { total: queue.uploadTotal, items: failed };
  clearTimeout(failureTimer);
  failureTimer = setTimeout(() => (failure.value = null), TOAST_DURATION_MS);
  return true;
}

onUnmounted(() => {
  clearTimeout(successTimer);
  clearTimeout(failureTimer);
  clearTimeout(uploadLingerTimer);
  clearTimeout(aiLingerTimer);
});
</script>

<style lang="scss" module>
.stack {
  position: fixed;
  bottom: var(--space-component-padding-2xLarge);
  right: var(--space-component-padding-2xLarge);
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-small);
}
.toast {
  width: 320px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-small);
  padding: var(--space-component-padding-large);
  border-radius: var(--border-radius-large);
  background-color: var(--color-interaction-neutral-subtle-normal);
  color: var(--color-content-primary);
  box-shadow:
    0px 8px 16px -2px var(--color-coolGreyA-p3),
    0px 2px 2px -1px var(--color-coolGreyA-p6);

  &.success {
    background-color: var(--color-background-positive-strong);
    color: var(--color-content-primary-inverted);
  }

  &.failure {
    background-color: var(--color-background-negative-strong);
    color: var(--color-content-primary-inverted);
  }

  /* SCASvgIcon sets fill directly on its own <svg>, so a plain `fill` on .success never reaches it —
     only a selector that targets the rendered svg itself can override it. */
  &.success :global(svg),
  &.failure :global(svg) {
    fill: var(--color-content-primary-inverted);
  }
}
.failureList {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-xSmall);
  /* A folder drop can fail on dozens of files; the popup keeps its size and scrolls instead. */
  max-height: 120px;
  overflow-y: auto;
}
.failureItem {
  @include Globals.typo-body-small;
  color: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.row {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-large);
  width: 100%;
}
.icon {
  flex: 0 0 auto;
}
.sparkleIcon {
  flex: 0 0 auto;
}
/* Same override as .success above: SCASvgIcon sets fill directly on its own <svg>. */
.sparkleIcon :global(svg) {
  fill: var(--color-interaction-default-active);
}
.content {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-xSmall);
  flex: 1;
  min-width: 0;
}
.titleRow {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-large);
}
.closeButton {
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--size-2xSmall);
  height: var(--size-2xSmall);

  &:hover {
    opacity: 0.7;
  }
}
.title {
  @include Globals.typo-body-medium;
  color: inherit;
  flex: 1;
  min-width: 0;
}
.meta {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small);
  @include Globals.typo-body-small;
  color: var(--color-content-secondary);

  .percentage {
    margin-left: auto;
  }
}

@include Globals.toast-transitions;

.leaveActive {
  right: 0;
}
</style>
